// @ts-nocheck

import * as R from 'ramda';
import React, { Component } from 'react';
import BranchLoader from '../../../common/components/core/BranchLoader';
import LineChart from '../../../common/components/core/LineChart';
import {
  generateDistributionTickValues,
  processDistributionData,
  processDistributionDataWithFilters,
} from '../../../common/support/utils/druid/distribution';
import AnalyticsNoDataMessage from '../../../main/components/AnalyticsNoDataMessage';
import { roundToFixedN, toPercentageStr } from '../../../main/support/utils';
import type { GroupByResponse, Query } from '../../../main/types/druidTypes';
const DEFAULT_TICK_VALUES = [0, 10, 15, 20, 25];
type Props = {
  /**
   * main query result.
   */
  data: GroupByResponse;

  /**
   * main query dispatched.
   */
  query: Query;

  /**
   * result of any additional queries.
   */
  additionalData: Record<string, GroupByResponse>;

  /**
   * each filter corresponds to a separate series.
   */
  filters: Array<string>;

  /**
   * dimensions that map to the chosen filters.
   */
  dimensions: Array<string>;

  /**
   * values to be displayed on the x axis.
   * e.g. seconds, hours, days.
   */
  domain: string;

  /**
   * errors resulting from druid queries.
   */
  error: Record<string, any>;

  /**
   * a mapping between the time domain chosen and the corresponding span and interval
   * e.g. seconds maps to a span of [0, 60] and an interval of 5 seconds.
   * {
   *    seconds_from_last_attributed_touch_to_event: {
   *      span: [0, 60],
   *      interval: 5
   *    },
   *    hours_from_last_attributed_touch_to_event: {
   *      span: [0, 24],
   *      interval: 1
   *    }
   * }
   */
  domainMap: Record<string, any>;

  /**
   * whether to show unique data.
   */
  unique: boolean;

  /**
   * additional x Axis properties.
   */
  xAxis: Record<string, any>;

  /**
   * additional y Axis properties.
   */
  yAxis: Record<string, any>;

  /**
   * product for which data is being graphed.
   */
  product: string;
};

class DistributionChart extends Component<Props> {
  render() {
    const { data, query, additionalData, filters, dimensions, domain, error } = this.props;
    let tickValues = {};
    let distributionDataProcessed = {};
    let additionalDataProcessed = {};
    const domainMetaData = this.props.domainMap[domain];
    tickValues = domainMetaData
      ? generateDistributionTickValues(domainMetaData.span, domainMetaData.interval)
      : DEFAULT_TICK_VALUES;
    const metric = this.props.unique ? 'unique_count' : 'total_count';

    if (data && !R.isEmpty(data)) {
      distributionDataProcessed = processDistributionDataWithFilters(
        data,
        query,
        filters,
        dimensions,
        tickValues,
        domain,
        metric
      );
    }

    // want to make sure that additionalData is not null, undefined, an empty object or an object where some
    // of it's values are null
    if (additionalData && !R.isEmpty(additionalData) && R.findIndex(R.isNil, R.values(additionalData)) < 0) {
      additionalDataProcessed = processDistributionData(additionalData, query, tickValues, domain, metric);
      distributionDataProcessed = R.merge(distributionDataProcessed, additionalDataProcessed);
    }

    if (error) {
      return (
        <div className="analytics-display">
          <AnalyticsNoDataMessage analyticsType="timeseries" message={error} product={this.props.product} />
        </div>
      );
    }

    if (!data) {
      return (
        <div className="analytics-display">
          <BranchLoader />
        </div>
      );
    }

    if (R.keys(distributionDataProcessed).length === 0) {
      return (
        <div className="analytics-display">
          <AnalyticsNoDataMessage analyticsType="timeseries" product={this.props.product} />
        </div>
      );
    }

    return (
      <div className="analytics-display">
        <LineChart
          xAxis={{ ...this.props.xAxis, tickValues }}
          yAxis={{ ...this.props.yAxis, tickFormat: (v) => toPercentageStr(roundToFixedN(4, v)) }}
          lines={distributionDataProcessed}
          xType="linear"
        />
      </div>
    );
  }
}

export default DistributionChart;
